/**
 * IGK CRO PDP — Hero controller
 * File: assets/pdp-hero.js
 *
 * Multi-product, fully reactive:
 *  - Variant matching from option selections
 *  - Live price / compare-at / discount updates
 *  - Recharge native selling plans: per-plan price via
 *    variant.selling_plan_allocations; submits `selling_plan`
 *  - Purchase-type toggle (subscribe vs one-time)
 *  - Quantity bundle tiles
 *  - Free-shipping nudge
 *  - AJAX add-to-cart with fallback + sticky bar sync
 *
 * No dependencies. Re-inits per section (Shopify editor friendly).
 */
(function () {
  'use strict';

  function initHero(root) {
    if (!root || root.__pdpInit) return;
    root.__pdpInit = true;

    var $ = function (s, c) { return (c || root).querySelector(s); };
    var $$ = function (s, c) { return Array.prototype.slice.call((c || root).querySelectorAll(s)); };

    /* ---- Parse product data ---- */
    var dataEl = $('[data-pdp-product-json]');
    if (!dataEl) return;
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var freeShipThreshold = parseInt(root.getAttribute('data-free-shipping-threshold'), 10) || 0;

    var form = $('#pdp-product-form');
    var variantInput = $('[data-variant-id]');
    var quantityInput = $('[data-quantity]');
    var sellingPlanInput = $('[data-selling-plan-input]');
    var priceEl = $('[data-price]');
    var compareEl = $('[data-compare-price]');
    var discountEl = $('[data-discount-badge]');
    var subPriceEl = $('[data-subscribe-price]');
    var oneTimePriceEl = $('[data-onetime-price]');
    var ctaPriceEl = $('[data-cta-price]');
    var ctaError = $('[data-cta-error]');
    var nudgeText = $('[data-nudge-text]');
    var planSelect = $('[data-selling-plan-select]');
    var dynamicCheckout = $('[data-dynamic-checkout]');

    /* ---- Money formatter (Shopify-compatible subset) ---- */
    function formatMoney(cents) {
      if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
      var value = (cents / 100).toFixed(2);
      var parts = value.split('.');
      var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var withCents = dollars + '.' + parts[1];
      var noTrailing = parts[1] === '00' ? dollars : withCents;
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, withCents)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, dollars)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCents)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dollars)
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, dollars);
    }

    /* ---- State ---- */
    var state = {
      variant: product.variants.filter(function (v) { return v.id === parseInt(variantInput.value, 10); })[0] || product.variants[0],
      purchaseType: ($('[data-purchase-radio]:checked') && $('[data-purchase-radio]:checked').value) || 'one-time',
      sellingPlanId: planSelect ? parseInt(planSelect.value, 10) : null,
      quantity: 1,
      adding: false,
      firstRender: true
    };

    /* ---- Find variant from selected options ---- */
    function getVariantById(id) {
      return product.variants.filter(function (v) { return v.id === id; })[0] || null;
    }
    function getSelectedVariant() {
      // Direct variant pick (single-option products: pills + bundle tiles)
      var picked = $('[data-variant-radio]:checked');
      if (picked) {
        var byId = getVariantById(parseInt(picked.value, 10));
        if (byId) return byId;
      }
      // Multi-option products: match by option values
      var selected = $$('[data-option-input]:checked').sort(function (a, b) {
        return (+a.dataset.optionPosition) - (+b.dataset.optionPosition);
      }).map(function (i) { return i.value; });

      if (!selected.length) return product.variants[0];

      return product.variants.filter(function (v) {
        return v.options.length === selected.length && v.options.every(function (opt, i) { return opt === selected[i]; });
      })[0] || null;
    }

    /* ---- Get subscription allocation for current variant + plan ---- */
    function getAllocation(variant, planId) {
      if (!variant || !planId) return null;
      return (variant.selling_plan_allocations || []).filter(function (a) {
        return a.selling_plan_id === planId;
      })[0] || null;
    }

    /* ---- Compute the active price (cents) for current state ---- */
    function activePrice() {
      if (!state.variant) return 0;
      if (state.purchaseType === 'subscribe') {
        var alloc = getAllocation(state.variant, state.sellingPlanId);
        if (alloc) return alloc.price;
      }
      return state.variant.price;
    }

    /* ---- Render all price-dependent UI ---- */
    function render() {
      var v = state.variant;

      // Sold out / unavailable handling
      var cta = $('[data-add-to-cart]');
      var ctaText = $('[data-cta-text]');
      if (!v) {
        if (cta) { cta.disabled = true; }
        if (ctaText) ctaText.textContent = 'Unavailable';
        return;
      }
      if (cta) cta.disabled = !v.available;
      if (ctaText && !v.available) ctaText.textContent = 'Sold Out';
      if (ctaText && v.available) ctaText.textContent = (cta && cta.dataset.label) || ctaText.dataset.default || ctaText.textContent;

      var price = activePrice();

      // Main price
      if (priceEl) priceEl.textContent = formatMoney(price);

      // Compare-at + discount: prefer the subscription compare if subscribing
      var compareAt = v.compare_at_price;
      if (state.purchaseType === 'subscribe') {
        var alloc = getAllocation(v, state.sellingPlanId);
        if (alloc && alloc.compare_at_price) compareAt = alloc.compare_at_price;
        // If subscribing and no explicit compare, show one-time price as the "was"
        if (!compareAt && alloc && v.price > alloc.price) compareAt = v.price;
      }
      if (compareEl && discountEl) {
        if (compareAt && compareAt > price) {
          compareEl.hidden = false;
          compareEl.querySelector('s').textContent = formatMoney(compareAt);
          discountEl.hidden = false;
          discountEl.textContent = Math.round((compareAt - price) * 100 / compareAt) + '% OFF';
        } else {
          compareEl.hidden = true;
          discountEl.hidden = true;
        }
      }

      // Purchase-type tile prices
      if (oneTimePriceEl) oneTimePriceEl.textContent = formatMoney(v.price);
      if (subPriceEl) {
        var subAlloc = getAllocation(v, state.sellingPlanId);
        subPriceEl.textContent = subAlloc ? formatMoney(subAlloc.price) : '';
      }

      // CTA price hint (shows total incl. quantity)
      if (ctaPriceEl) {
        var total = price * state.quantity;
        ctaPriceEl.textContent = state.quantity > 1 ? ' — ' + formatMoney(total) : '';
      }

      // Selling plan input (Recharge): only enabled when subscribing
      if (sellingPlanInput) {
        if (state.purchaseType === 'subscribe' && state.sellingPlanId) {
          sellingPlanInput.disabled = false;
          sellingPlanInput.value = state.sellingPlanId;
        } else {
          sellingPlanInput.disabled = true;
          sellingPlanInput.value = '';
        }
      }

      // Hide native dynamic-checkout button for subscriptions (Shop Pay can't carry selling_plan reliably)
      if (dynamicCheckout) {
        dynamicCheckout.style.display = state.purchaseType === 'subscribe' ? 'none' : '';
      }

      // Variant id + quantity in form
      if (variantInput) variantInput.value = v.id;
      if (quantityInput) quantityInput.value = state.quantity;

      // URL ?variant=
      if (history.replaceState && v.id) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url.toString());
      }

      // Featured media swap — but NOT on initial load (keep the first gallery image)
      if (!state.firstRender && v.featured_media_id) activateMediaById(v.featured_media_id);

      // Shipping nudge
      updateNudge(price * state.quantity);

      // Sticky bar sync
      syncSticky(v, price);

      // Free-gift threshold nudge
      syncGiftNudge(price);
    }

    /* ---- Free-gift threshold nudge ---- */
    var giftThreshold = parseInt(root.getAttribute('data-free-gift-threshold'), 10) || 0;
    var giftCallout = $('[data-gift-callout]');
    var giftDesc = $('[data-gift-desc]');
    var giftNudge = $('[data-gift-nudge]');
    var giftDescOriginal = giftDesc ? giftDesc.textContent : '';

    // Cheapest available variant that clears the threshold — prefer a bundle.
    function cheapestQualifier() {
      var ok = product.variants.filter(function (v) { return v.available && v.price >= giftThreshold; });
      if (!ok.length) return null;
      ok.sort(function (a, b) { return a.price - b.price; });
      var bundles = ok.filter(function (v) {
        var t = (v.title || '').toLowerCase();
        return t.indexOf('get') > -1 && t.indexOf('free') > -1;
      });
      return bundles[0] || ok[0];
    }

    function syncGiftNudge(priceCents) {
      if (!giftCallout || !giftThreshold) return;
      var qualifies = priceCents >= giftThreshold;
      giftCallout.classList.toggle('is-unlocked', qualifies);

      if (qualifies) {
        if (giftDesc) giftDesc.textContent = 'Unlocked — your free mystery gift is in the bag! 🎉';
        if (giftNudge) { giftNudge.hidden = true; giftNudge.removeAttribute('data-target-variant'); }
        return;
      }

      // Below threshold → nudge toward the cheapest qualifying option.
      if (giftDesc) giftDesc.textContent = giftDescOriginal;
      var target = cheapestQualifier();
      if (giftNudge && target) {
        giftNudge.hidden = false;
        giftNudge.textContent = 'Unlock your free gift — choose ' + target.title + ' →';
        giftNudge.setAttribute('data-target-variant', target.id);
      } else if (giftNudge) {
        giftNudge.hidden = true;
      }
    }

    if (giftNudge) {
      giftNudge.addEventListener('click', function () {
        var id = parseInt(giftNudge.getAttribute('data-target-variant'), 10);
        if (!id) return;
        var el = document.querySelector('[data-bundle-tile][data-variant-id="' + id + '"]')
              || document.querySelector('[data-variant-pill][data-variant-id="' + id + '"]');
        if (el) selectVariantEl(el);
      });
    }

    /* ---- Shipping nudge ---- */
    function updateNudge(totalCents) {
      if (!nudgeText || !freeShipThreshold) return;
      var gap = freeShipThreshold - totalCents;
      nudgeText.innerHTML = gap > 0
        ? "You're <strong>" + formatMoney(gap) + "</strong> away from <strong>free shipping</strong>"
        : "🎉 You unlocked <strong>free shipping!</strong>";
    }

    /* ---- Gallery ---- */
    function activateSlide(idx) {
      $$('[data-slide]').forEach(function (s) { s.classList.toggle('is-active', s.dataset.slide === String(idx)); });
      $$('[data-target-slide]').forEach(function (t) { t.classList.toggle('is-active', t.dataset.targetSlide === String(idx)); });
    }
    function activateMediaById(mediaId) {
      var slide = $('[data-media-id="' + mediaId + '"]');
      if (slide) activateSlide(slide.dataset.slide);
    }
    $$('[data-target-slide]').forEach(function (t) {
      t.addEventListener('click', function () { activateSlide(t.dataset.targetSlide); });
    });

    /* ---- Option pills ---- */
    $$('[data-option-pill]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        var input = pill.querySelector('input');
        if (input) input.checked = true;
        var group = pill.closest('.pdp-hero__option-pills');
        $$('[data-option-pill]', group).forEach(function (p) { p.classList.remove('is-selected'); });
        pill.classList.add('is-selected');
        state.variant = getSelectedVariant();
        render();
      });
    });

    /* ---- Purchase type ---- */
    $$('[data-purchase-type]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        $$('[data-purchase-type]').forEach(function (o) { o.classList.remove('is-selected'); });
        opt.classList.add('is-selected');
        var radio = opt.querySelector('[data-purchase-radio]');
        if (radio) radio.checked = true;
        state.purchaseType = opt.dataset.purchaseType;
        render();
      });
    });

    /* ---- Selling plan frequency ---- */
    if (planSelect) {
      planSelect.addEventListener('change', function () {
        state.sellingPlanId = parseInt(planSelect.value, 10);
        // selecting a frequency implies subscribe
        var subOpt = $('[data-purchase-type="subscribe"]');
        if (subOpt && state.purchaseType !== 'subscribe') subOpt.click();
        else render();
      });
    }

    /* ---- Variant pills + bundle tiles (single-option products) ---- */
    /* ---- Bundles only apply to the single size; show/hide accordingly ---- */
    var bundleSection = $('[data-bundle-section]');
    function syncBundleVisibility() {
      if (!bundleSection) return;
      var requiredId = parseInt(bundleSection.getAttribute('data-bundle-requires'), 10);
      var pickedBundle = $('[data-bundle-tile] [data-variant-radio]:checked');
      var sizePicked = $('[data-variant-pill] [data-variant-radio]:checked');
      // Show when: a bundle is selected, OR the bundle's size is selected,
      // OR no size pill exists at all (defensive).
      var show = pickedBundle || !sizePicked || (parseInt(sizePicked.value, 10) === requiredId);
      bundleSection.style.display = show ? '' : 'none';
    }

    function selectVariantEl(el) {
      var radio = el.querySelector('[data-variant-radio]');
      if (!radio || radio.disabled) return;
      radio.checked = true;
      $$('[data-variant-pill]').forEach(function (p) { p.classList.remove('is-selected'); });
      $$('[data-bundle-tile]').forEach(function (t) { t.classList.remove('is-selected'); });
      el.classList.add('is-selected');
      state.quantity = 1;
      state.variant = getSelectedVariant();
      render();
      syncBundleVisibility();
    }
    $$('[data-variant-pill]').forEach(function (pill) {
      pill.addEventListener('click', function () { selectVariantEl(pill); });
    });
    $$('[data-bundle-tile]').forEach(function (tile) {
      tile.addEventListener('click', function () { selectVariantEl(tile); });
    });
    syncBundleVisibility();

    /* ---- Sticky bar ---- */
    function syncSticky(v, price) {
      var bar = document.querySelector('[data-sticky-bar]');
      if (!bar) return;
      var sp = bar.querySelector('[data-sticky-price]');
      var sl = bar.querySelector('[data-sticky-label]');
      if (sp) sp.textContent = formatMoney(price);
      if (sl) sl.textContent = state.purchaseType === 'subscribe' ? 'Subscribe & Save' : '';
    }

    /* ---- AJAX add to cart (single-fire, lock-protected) ---- */
    function addToCart(e) {
      if (e) { e.preventDefault(); if (e.stopImmediatePropagation) e.stopImmediatePropagation(); }
      // Hard lock: ignore any concurrent/duplicate trigger while a request is in flight.
      if (state.adding) return;
      var cta = $('[data-add-to-cart]');
      var ctaText = $('[data-cta-text]');
      if (!cta || cta.disabled) return;
      if (!variantInput || !variantInput.value) return;
      if (ctaError) { ctaError.hidden = true; ctaError.textContent = ''; }

      state.adding = true;
      var original = ctaText ? ctaText.textContent : '';
      cta.disabled = true;
      if (ctaText) ctaText.textContent = 'Adding…';

      function release() {
        state.adding = false;
        cta.disabled = false;
        if (ctaText) ctaText.textContent = original;
      }

      var body = {
        items: [{
          id: parseInt(variantInput.value, 10),
          quantity: state.quantity
        }]
      };
      if (state.purchaseType === 'subscribe' && state.sellingPlanId) {
        body.items[0].selling_plan = state.sellingPlanId;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function () {
          release();
          // Stay on the page; notify the theme's cart drawer/count via events.
          document.dispatchEvent(new CustomEvent('pdp:added', { bubbles: true }));
          ['cart:refresh', 'cart:open', 'cart-drawer:open'].forEach(function (evt) {
            document.dispatchEvent(new CustomEvent(evt, { bubbles: true }));
          });
        })
        .catch(function (err) {
          release();
          if (ctaError) {
            ctaError.hidden = false;
            ctaError.textContent = (err && err.description) || 'Could not add to cart. Please try again.';
          }
        });
    }

    /* ---- Bind ONCE. The CTA is type="button"; we never use native form submit,
            so the theme's own product-form handler can't also fire. The dataset
            guard prevents duplicate listeners if the section re-initialises. ---- */
    var ctaBtn = $('[data-add-to-cart]');
    if (ctaBtn && !ctaBtn.dataset.pdpBound) {
      ctaBtn.dataset.pdpBound = '1';
      ctaBtn.addEventListener('click', addToCart);
    }
    // Belt-and-suspenders: if anything still triggers a form submit, swallow it.
    if (form && !form.dataset.pdpBound) {
      form.dataset.pdpBound = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        addToCart();
      });
    }

    /* ---- Sticky CTA forwards click ---- */
    var stickyCta = document.querySelector('[data-sticky-atc]');
    if (stickyCta && !stickyCta.dataset.pdpBound) {
      stickyCta.dataset.pdpBound = '1';
      stickyCta.addEventListener('click', function () { addToCart(); });
    }

    /* ---- Initial render ---- */
    // Default selling plan + subscribe default
    if (planSelect && !state.sellingPlanId) state.sellingPlanId = parseInt(planSelect.value, 10);
    render();
    state.firstRender = false; // subsequent renders swap the gallery image on variant change

    /* ---- Sticky bar visibility ---- */
    var stickyBar = document.querySelector('[data-sticky-bar]');
    var ctaForObserver = $('[data-add-to-cart]');
    if (stickyBar && ctaForObserver && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        stickyBar.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(ctaForObserver);
    }
  }

  function initAll() { document.querySelectorAll('[data-pdp-hero]').forEach(initHero); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else { initAll(); }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-pdp-hero]');
    if (el) { el.__pdpInit = false; initHero(el); }
  });
})();